// Picture-in-picture (meeting room): a compact, always-on-top window that keeps
// the meeting visible when you leave the tab — switch away, minimise, or (the
// main case) start screen sharing. Built on the Chromium Document
// Picture-in-Picture API, which pops a real HTML subtree (our tiles + a slim
// control strip) into an OS-level window. The SAME already-decrypted tiles are
// re-parented there — nothing is re-encoded and no media leaves the client, so
// E2EE is untouched. The API is absent on Firefox/Safari; the pop-out button and
// the auto setting simply hide there (see `pipSupported`).

import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

interface DocumentPiP {
  requestWindow(options: { width: number; height: number }): Promise<Window>;
}

/** Chromium-only. Everything PiP is gated on this — the button never shows and
 * the auto-open never fires where it is false. */
export const pipSupported =
  typeof window !== 'undefined' && 'documentPictureInPicture' in window;

const AUTO_KEY = 'bz:pip-auto';

function loadAuto(): boolean {
  try {
    return localStorage.getItem(AUTO_KEY) !== '0'; // default ON
  } catch {
    return true;
  }
}

interface PipView {
  /** The open PiP window, or null. Its lifetime is owned here. */
  window: Window | null;
  /** Auto-open the moment the local user starts screen sharing. */
  autoOnShare: boolean;
  /** Collapsed to the control bar only (the people/screen are hidden and the
   * window is shrunk to the controls). */
  minimized: boolean;
}

/** The two window shapes: the full compact meeting, and the controls-only bar. */
const NORMAL_SIZE = { width: 320, height: 300 };
const MINI_SIZE = { width: 272, height: 92 };

const pipStore = createStore<PipView>(() => ({
  window: null,
  autoOnShare: loadAuto(),
  minimized: false,
}));

export function usePipStore<T>(selector: (s: PipView) => T): T {
  return useStore(pipStore, selector);
}

/** Dress the bare PiP document with the app's stylesheets + theme so our tiles
 * render identically there. INLINE the rules as <style> (the Chrome-documented
 * way) rather than re-<link>ing the external file: an about:blank PiP window
 * re-linking the app's `crossorigin` stylesheet does not reliably load it in a
 * real browser (headless masks this), whereas inlined same-origin cssRules apply
 * synchronously and CSP allows them (style-src 'unsafe-inline'). A genuinely
 * cross-origin sheet (fonts, etc.) throws on cssRules — re-link those. */
function dressWindow(w: Window): void {
  const doc = w.document;
  doc.documentElement.className = document.documentElement.className;
  const theme = document.documentElement.getAttribute('data-theme');
  if (theme) doc.documentElement.setAttribute('data-theme', theme);
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const cssText = Array.from(sheet.cssRules)
        .map((r) => r.cssText)
        .join('\n');
      const style = doc.createElement('style');
      style.textContent = cssText;
      doc.head.appendChild(style);
    } catch {
      // cross-origin sheet: cssRules is unreadable, so re-link it by URL.
      if (sheet.href) {
        const link = doc.createElement('link');
        link.rel = 'stylesheet';
        link.href = sheet.href;
        doc.head.appendChild(link);
      }
    }
  }
  doc.title = 'Brizina meeting';
  doc.body.style.margin = '0';
}

/** Pop the meeting out into a floating window. No-op if unsupported or already
 * open. Compact by design — small tiles, four to a row (the CSS in styles.css). */
export async function openPip(): Promise<void> {
  if (!pipSupported || pipStore.getState().window) return;
  try {
    const dpip = (window as unknown as { documentPictureInPicture: DocumentPiP })
      .documentPictureInPicture;
    const w = await dpip.requestWindow(NORMAL_SIZE);
    dressWindow(w);
    // The user closing the PiP chrome (or the window going away) tears it down;
    // mirror that into state so the button and portal follow.
    w.addEventListener('pagehide', () => pipStore.setState({ window: null, minimized: false }), {
      once: true,
    });
    pipStore.setState({ window: w, minimized: false });
  } catch {
    // permission dismissed, or another PiP owns the slot — leave it closed
  }
}

export function closePip(): void {
  const w = pipStore.getState().window;
  if (w) w.close();
  pipStore.setState({ window: null, minimized: false });
}

/** Collapse the PiP to a controls-only bar (or restore it). Shrinks the actual
 * window; if the browser refuses the resize, the CSS still hides the people and
 * screen, so it is controls-only either way. */
export function toggleMinimize(): void {
  const { window: w, minimized } = pipStore.getState();
  const next = !minimized;
  pipStore.setState({ minimized: next });
  if (!w) return;
  const to = next ? MINI_SIZE : NORMAL_SIZE;
  try {
    // resizeTo needs the click's user activation and anchors the window's
    // top-left corner; the browser does NOT allow moving a PiP window
    // (moveTo/moveBy are silent no-ops), so it resizes in place from there.
    w.resizeTo(to.width, to.height);
  } catch {
    // resize not permitted (no activation) — the content collapse still applies
  }
}

export function togglePip(): void {
  if (pipStore.getState().window) closePip();
  else void openPip();
}

/** Turn the auto-open-on-share behaviour on or off (persisted). The manual
 * pop-out button is unaffected. */
export function setAutoOnShare(on: boolean): void {
  try {
    localStorage.setItem(AUTO_KEY, on ? '1' : '0');
  } catch {
    // private mode — in-memory only for this session
  }
  pipStore.setState({ autoOnShare: on });
}
